import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProps,
} from "@hello-pangea/dnd";
import { Box, Button } from "@mui/material";
import { ReactNode, useCallback, useEffect, useState, type JSX } from "react";
import DefaultItemRenderer from "@/components/common/verticalDraggableList/defaultItemRenderer";
import { VIOLET } from "@/config/mui/colorPalette";
import { DraggableItemType } from "@/hooks/common/useDraggableItems";

export const reorder = <T,>(
  list: T[],
  startIndex: number,
  endIndex: number
): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export type DraggableItemRenderer = (props: {
  selected: boolean;
  content: ReactNode;
  enabled: boolean;
}) => JSX.Element;

type Props = {
  enabled?: boolean;
  items: DraggableItemType[];
  updateItemsIndex: (items: DraggableItemType[]) => void;
  itemRenderer?: DraggableItemRenderer;
  isNested?: boolean;
};

export const VerticalDraggableList = ({
  enabled = true,
  items,
  updateItemsIndex,
  itemRenderer,
  isNested = false,
}: Props) => {
  const onDragEnd = useCallback(
    (result: DropResult) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      const newItems = reorder(
        items,
        result.source.index,
        result.destination.index
      );

      updateItemsIndex(newItems);
    },
    [items, updateItemsIndex]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable>
        {(provided, snapshot) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              border: snapshot.isDraggingOver
                ? `1px solid ${VIOLET[40]}`
                : "1px solid transparent",
              backgroundColor: "transparent",
              borderRadius: "4px",
              transition: "border 0.2s ease-in-out",
              pl: isNested ? 4 : 0,
            }}
          >
            {items.map((item, index) => (
              <Draggable
                key={item.id}
                draggableId={item.id}
                index={index}
                isDragDisabled={!enabled}
              >
                {(provided, snapshot) =>
                  isNested ? (
                    <Button
                      key={item.id}
                      onClick={(e) => {
                        // Prevent dragging parent item with propagation
                        e.stopPropagation();
                      }}
                      disableRipple
                      sx={{ ...resetStyleProps }}
                    >
                      <DraggableBoxRender
                        item={item}
                        provided={provided}
                        snapshot={snapshot}
                        index={index}
                        itemRenderer={itemRenderer}
                        enabled={enabled}
                        isNested={isNested}
                      />
                    </Button>
                  ) : (
                    <DraggableBoxRender
                      item={item}
                      provided={provided}
                      snapshot={snapshot}
                      index={index}
                      itemRenderer={itemRenderer}
                      enabled={enabled}
                      isNested={isNested}
                    />
                  )
                }
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

const resetStyleProps = {
  width: "100%",
  backgroundColor: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "grab",
  color: "inherit",
};

const DraggableBoxRender = ({
  provided,
  snapshot,
  index,
  itemRenderer,
  enabled,
  isNested,
  item,
}: {
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  index: number;
  itemRenderer: DraggableItemRenderer;
  enabled: boolean;
  isNested: boolean;
  item: DraggableItemType;
}) => {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      sx={{
        width: "100%",
        userSelect: "none",
        mt: index > 0 ? 1 : 0,
        ...provided.draggableProps.style,
      }}
    >
      {itemRenderer
        ? itemRenderer({
            selected: snapshot.isDragging,
            content: item.content,
            enabled,
          })
        : DefaultItemRenderer({
            selected: snapshot.isDragging,
            content: item.content,
            enabled,
            isNested,
          })}
    </Box>
  );
};

// Necessary for StrictMode in React 18
// https://github.com/atlassian/react-beautiful-dnd/issues/2399#issuecomment-1175638194
export const StrictModeDroppable = ({
  children,
  ...props
}: Omit<DroppableProps, "droppableId">) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <Droppable droppableId="droppable" {...props}>
      {children}
    </Droppable>
  );
};
